import { circle, Units } from '@turf/turf';
import { ICo2SequestrationData, IPaginationData } from 'common/defines/clients';
import { TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import { sizeByZoomLevel } from 'common/utils/util';
import { QUERY_KEY } from 'constants/constants';
import { useGetClientSettingMapView } from 'hooks/map-view/useGetClientSettingMapView';
import { useClientData } from 'hooks/useClientData';
import { useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl';
import { useQuery } from 'react-query';
import { getDataCo2SequestrationAnalyticPagination } from 'services/analytics/apiAnalyticsConfig.services';
import { useAppSelector } from 'store/hooks';
import { co2SequestrationAnalyticsSelector } from 'store/slices/map-view/co2SequestrationAnalytics';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import { rightBarSelector } from 'store/slices/rightBarSlice';

export const useCo2SequestrationAnalytics = ({ mapRef }: any) => {
  const [co2SequestrationData, setCo2SequestrationData] = useState<ICo2SequestrationData[]>([]);
  const [page, setPage] = useState<number>(1);

  const {
    isLayer3D,
    isInEditTab,
    analyticId,
    mapViewState: { zoom },
  } = useAppSelector(mapViewSelector);
  const { analyticName } = useAppSelector(rightBarSelector);
  const { dataCo2SequestrationInterval } = useAppSelector(co2SequestrationAnalyticsSelector);
  const { getTopSurfaceOpacity, colorTransparent, isSameColorTopSurface, getOpacityExtrusion } =
    useGetClientSettingMapView();
  const { contourSetting } = useClientData();
  // const { isFixedPoint, value } = useClientDataConfig();

  useQuery(
    [QUERY_KEY.CO2_SEQUESTRATION_ANALYSIS, analyticId, page],
    () => getDataCo2SequestrationAnalyticPagination({ analysisId: analyticId || '', page }),
    {
      enabled: !!analyticId && analyticName === TYPE_ANALYTICS_MAP_VIEW.CO2_SEQUESTRATION,
      onSuccess(res) {
        const resData = res.data as IPaginationData;
        const hasNextPage = resData.hasNextPage;
        if (page === 1) {
          setCo2SequestrationData([...resData.docs]);
        } else {
          setCo2SequestrationData([...co2SequestrationData, ...resData.docs]);
        }
        if (hasNextPage) setPage(page + 1);
      },
    }
  );

  // reset data when change analyticId
  useEffect(() => {
    setCo2SequestrationData([]);
    setPage(1);
  }, [analyticId]);

  const ranges = dataCo2SequestrationInterval.range;

  const geoJsonData: GeoJSON.FeatureCollection = useMemo(() => {
    const evaluateColorValue = (item: ICo2SequestrationData) => {
      for (let range of ranges) {
        if (item.co2Sequestration >= range.from && item.co2Sequestration < range.to) {
          return range.visible ? range.color : 'transparent';
        }
      }
    };

    const featuresWithCirclePoint: GeoJSON.Feature[] = co2SequestrationData?.map((item) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.longX, item.latY],
      },
      properties: { color: evaluateColorValue(item) ?? 'transparent', ...item },
    }));

    const generateGeometryWithCircle = (item: ICo2SequestrationData) => {
      const center = [item.longX, item.latY];
      const radius = sizeByZoomLevel(zoom) * 1000;
      const options = {
        units: 'meters' as Units,
        properties: { color: evaluateColorValue(item) ?? 'transparent', ...item },
      };
      const circleGeometry = circle(center, radius, options);
      return circleGeometry;
    };

    return {
      type: 'FeatureCollection',
      features: isLayer3D
        ? co2SequestrationData?.map((item) => generateGeometryWithCircle(item))
        : (featuresWithCirclePoint as GeoJSON.Feature[]),
    };
  }, [isLayer3D, ranges, co2SequestrationData, zoom]);

  const layerCo2Sequestration = useMemo(() => {
    if (!ranges?.length) {
      return null;
    }
    return (
      geoJsonData &&
      !isInEditTab && (
        <Source id={`co2Sequestration-point`} type="geojson" data={geoJsonData}>
          {/* 2D Point */}
          <Layer
            id={`fill-top-surface-point`}
            type="circle"
            source={`co2Sequestration-circle-point`}
            paint={{
              'circle-color': ['get', 'color'],
              'circle-radius': 5,
            }}
            layout={{ visibility: !isLayer3D ? 'visible' : 'none' }}
          />
          {/* 3D */}
          <Layer
            id={`co2Sequestration-top-surface-point`}
            type="fill-extrusion"
            source={`co2Sequestration-circle-point`}
            filter={['!=', 'color', 'transparent']}
            paint={{
              'fill-extrusion-color': ['get', 'color'],
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': contourSetting,
              'fill-extrusion-opacity': getTopSurfaceOpacity,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: isLayer3D ? 'visible' : 'none' }}
          />
          <Layer
            id={`co2Sequestration-side-surface-point`}
            type="fill-extrusion"
            source={`co2Sequestration-circle-point`}
            filter={['!=', 'color', 'transparent']}
            paint={{
              'fill-extrusion-color': isSameColorTopSurface ? ['get', 'color'] : colorTransparent,
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': 0,
              'fill-extrusion-opacity': getOpacityExtrusion,
              // getOpacityExtrusion,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: isLayer3D ? 'visible' : 'none' }}
          />
        </Source>
      )
    );
  }, [
    colorTransparent,
    contourSetting,
    geoJsonData,
    getOpacityExtrusion,
    getTopSurfaceOpacity,
    isInEditTab,
    isLayer3D,
    isSameColorTopSurface,
    ranges?.length,
  ]);
  return { layerCo2Sequestration };
};
